import { useState } from 'react';
import { Check, Loader2, RotateCcw, X } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import type { SheetOpportunity } from '../../../hooks/useOpportunitySheet';
import { closePatch, type OpportunityPatch } from '../../../lib/crm/opportunitySheet';
import type { CRMPipelineStage } from '../../../types/crm';
import { BTN_GHOST, BTN_PRIMARY, BTN_SECONDARY } from '../../bureau/buttons';
import { FIELD } from '../../bureau/encre';

export type Closing = 'won' | 'lost' | null;

/** The header's gestures on how the deal ends: « Gagnée » / « Perdue » while open, « Rouvrir » after. */
export function OutcomeButtons({
  deal,
  busy,
  onClose,
  onReopen,
}: {
  deal: SheetOpportunity;
  busy: boolean;
  onClose: (outcome: 'won' | 'lost') => void;
  onReopen: () => void;
}) {
  if (deal.status !== 'open') {
    return (
      <button type="button" onClick={onReopen} disabled={busy} className={BTN_SECONDARY}>
        <RotateCcw size={15} aria-hidden="true" /> Rouvrir
      </button>
    );
  }
  return (
    <>
      <button type="button" onClick={() => onClose('won')} disabled={busy} className={BTN_SECONDARY}>
        <Check size={16} aria-hidden="true" /> Gagnée
      </button>
      <button type="button" onClick={() => onClose('lost')} disabled={busy} className={BTN_SECONDARY}>
        <X size={16} aria-hidden="true" /> Perdue
      </button>
    </>
  );
}

/**
 * Closing the deal, said before it is done (PLAN-ENCRE-OUTILS O3): won, it moves to the pipeline's
 * « Gagné » column and may open its onboarding lead — the choice the pipeline's dialog offered;
 * lost, it says why (`reason_lost`, what Otto writes too) and moves to « Perdu ».
 */
export function OutcomePanel({
  deal,
  stages,
  closing,
  busy,
  onApply,
  onCancel,
}: {
  deal: SheetOpportunity;
  stages: readonly CRMPipelineStage[];
  closing: 'won' | 'lost';
  busy: boolean;
  /** Writes the patch, then opens the onboarding lead when asked. */
  onApply: (patch: OpportunityPatch, withLead: boolean) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const spinner = busy && <Loader2 size={15} className="animate-spin" aria-hidden="true" />;
  return (
    <section aria-label={closing === 'won' ? 'Marquer gagnée' : 'Marquer perdue'} className="mb-8 rounded-2xl bg-desk-card p-4 shadow-lift">
      {closing === 'won' ? (
        <>
          <p className="text-sm text-desk-strong">
            L'affaire passe en « Gagné »{deal.lead ? '.' : ' : ouvrir aussi son lead d’onboarding ?'}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {deal.lead ? (
              <button type="button" disabled={busy} onClick={() => onApply(closePatch(deal, stages, 'won'), false)} className={BTN_PRIMARY}>
                {spinner} Marquer gagnée
              </button>
            ) : (
              <>
                <button type="button" disabled={busy} onClick={() => onApply(closePatch(deal, stages, 'won'), true)} className={BTN_PRIMARY}>
                  {spinner} Gagnée, et ouvrir le lead
                </button>
                <button type="button" disabled={busy} onClick={() => onApply(closePatch(deal, stages, 'won'), false)} className={BTN_SECONDARY}>
                  Gagnée seulement
                </button>
              </>
            )}
            <button type="button" disabled={busy} onClick={onCancel} className={BTN_GHOST}>
              Annuler
            </button>
          </div>
        </>
      ) : (
        <>
          <label className="block">
            <span className="mb-1.5 block text-sm text-desk-strong">Pourquoi est-elle perdue ?</span>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Prix, timing, concurrent… (facultatif)"
              className={twMerge(FIELD, 'h-auto resize-none py-2')}
            />
          </label>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" disabled={busy} onClick={() => onApply(closePatch(deal, stages, 'lost', reason), false)} className={BTN_PRIMARY}>
              {spinner} Marquer perdue
            </button>
            <button type="button" disabled={busy} onClick={onCancel} className={BTN_GHOST}>
              Annuler
            </button>
          </div>
        </>
      )}
    </section>
  );
}
